import { getNodeDataRecord, getOutputNodeDisplayType, getOutputNodeInputType, isUncataloguedNode, nodeWidth, uncataloguedNodeDef, type GraphEdge, type GraphNode, type NodeDefBase } from '../graph/nodeDefs.js';
import { RENDER_NODE_DEFS } from '../graph/renderNodeDefs.js';
import { clearDOMCache, getEl } from '../dom.js';
import { createDragger } from './controls/dragField.js';
import { markNodeDragHandle } from './nodeDragHandles.js';
import { renderCustomNodeUI } from './nodeRenderers.js';
import { renderUIRows } from './renderUISchema.js';
import { createNodePortLabel, createNodePortRow } from './nodeFieldEditorControls.js';
import type { NodeFieldCommands } from './nodeRenderers.js';
import type { RuntimeStateSchema } from '../graph/eventGraph.js';
import type { DataSourceDef, QueryGraphDef } from '../graph/dataGraph.js';
import type { RecordContextFieldOption } from '../state/recordContextSchema.js';
import type { SvgPathShape } from '../state/appState.js';

interface NodeCanvasDeps {
  edges: GraphEdge[];
  selectedNode: string | null;
  deleteNode: (id: string) => void;
  setupPortDrag: (dot: HTMLElement) => void;
  setupNodeDrag: (el: HTMLElement, node: GraphNode) => void;
  setNodeData: (nodeId: string, field: string, value: unknown) => void;
  nodeFieldCommands: NodeFieldCommands;
  renderGraph: () => void;
  scheduleRender: () => void;
  updateParams: () => void;
  runtimeState?: RuntimeStateSchema;
  dataSources?: Record<string, DataSourceDef>;
  shapes?: Record<string, SvgPathShape>;
  queryGraphs?: Record<string, QueryGraphDef>;
  recordContextFields?: Record<string, RecordContextFieldOption>;
  setRuntimeStateReference?: (node: GraphNode, field: string, stateName: string) => void;
  setLookupSource?: (node: GraphNode, sourceId: string) => void;
  setLookupField?: (node: GraphNode, field: string) => void;
  invokeNodeAction?: (node: GraphNode, action: 'convert-shape-to-path') => void;
  /** Node definitions to use. Defaults to RENDER_NODE_DEFS (geometry/material). */
  nodeDefs?: Record<string, NodeDefBase>;
}

export function renderNodeCanvas(nodes: GraphNode[], deps: NodeCanvasDeps): void {
  const wrapper = getEl('graph-canvas-wrapper');
  wrapper.querySelectorAll('.node').forEach(el => el.remove());
  clearDOMCache();

  const defs = deps.nodeDefs ?? RENDER_NODE_DEFS;

  for (const node of nodes) {
    // A kind the catalogue does not know is drawn opaque, never skipped: it
    // stays in the document, and its wires need sockets to end at.
    const uncatalogued = isUncataloguedNode(node, defs);
    const def = uncatalogued
      ? uncataloguedNodeDef(node, nodes, deps.edges, defs)
      : defs[node.type] as NodeDefBase;

    const el = document.createElement('div');
    el.className = `node cat-${def.cat}${uncatalogued ? ' uncatalogued' : ''}`;
    if (uncatalogued) el.title = `${node.type}: a node this editor does not know; it is kept as it is`;
    el.id = `node-${node.id}`;
    el.style.left = `${node.x}px`;
    el.style.top = `${node.y}px`;
    el.style.width = `${nodeWidth(node, uncatalogued ? { [node.type]: def } : defs)}px`;
    if (deps.selectedNode === node.id) el.classList.add('selected');

    const hdr = document.createElement('div');
    hdr.className = 'node-header';
    markNodeDragHandle(hdr, 'descendants');
    hdr.textContent = def.label;
    const cat = document.createElement('span');
    cat.className = 'cat';
    const catLabel = Array.isArray(def.cat) ? def.cat[0] : def.cat;
    cat.textContent = uncatalogued
      ? 'unknown'
      : node.type === 'Output'
        ? String(getOutputNodeDisplayType(node) || getOutputNodeInputType(node) || catLabel)
        : catLabel;
    hdr.appendChild(cat);
    el.appendChild(hdr);

    const del = document.createElement('button');
    del.className = 'node-del';
    del.textContent = '×';
    del.title = 'Delete';
    del.addEventListener('mousedown', e => {
      e.stopPropagation();
      deps.deleteNode(node.id);
    });
    hdr.appendChild(del);

    const ports = document.createElement('div');
    ports.className = 'node-ports';
    markNodeDragHandle(ports);

    // An uncatalogued node's def has no rows of its own: it falls through to
    // the plain sockets below.
    if (def.ui) {
      renderUIRows(node, ports, def, {
        scheduleRender: deps.scheduleRender,
        updateParams: deps.updateParams,
        setupPortDrag: deps.setupPortDrag,
        renderGraph: deps.renderGraph,
        setNodeData: deps.setNodeData,
        runtimeState: deps.runtimeState,
        dataSources: deps.dataSources,
        shapes: deps.shapes,
        queryGraphs: deps.queryGraphs,
        recordContextFields: deps.recordContextFields,
        graphNodes: nodes,
        graphEdges: deps.edges,
        setRuntimeStateReference: deps.setRuntimeStateReference,
        setLookupSource: deps.setLookupSource,
        setLookupField: deps.setLookupField,
        invokeNodeAction: deps.invokeNodeAction,
      });
    } else if (!renderCustomNodeUI(node, ports, {
      scheduleRender: deps.scheduleRender,
      updateParams: deps.updateParams,
      setupPortDrag: deps.setupPortDrag,
      renderGraph: deps.renderGraph,
      commands: deps.nodeFieldCommands,
    })) {
      const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
      const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;

      const nodeData = getNodeDataRecord(node);
      inputs.forEach((input, i) => {
        const row = createNodePortRow();
        const dot = document.createElement('div');
        dot.className = `port-dot in type-${input.type}`;
        dot.dataset.node = node.id;
        dot.dataset.port = String(i);
        dot.dataset.io = 'in';
        const lbl = createNodePortLabel(input.label ?? input.name);
        row.appendChild(dot);
        row.appendChild(lbl);

        if (input.range) {
          const d = input.range;
          const field = input.name;
          const dragger = createDragger(
            Number(nodeData[field] ?? def.defaults?.[field] ?? 0),
            d.min, d.max, d.step,
            v => {
              deps.setNodeData(node.id, field, v);
              deps.scheduleRender();
              deps.updateParams();
            },
            undefined,
            (d.subtype || d.unit) ? { subtype: d.subtype, unit: d.unit } : undefined,
          );
          dragger.style.width = '64px';
          dragger.style.marginLeft = '8px';
          dragger.style.marginRight = '8px';
          row.appendChild(dragger);
        }

        deps.setupPortDrag(dot);
        ports.appendChild(row);
      });

      outputs.forEach((out, i) => {
        const row = createNodePortRow();
        const lbl = createNodePortLabel(out.label ?? out.name, 'out');
        const dot = document.createElement('div');
        dot.className = `port-dot out type-${out.type}`;
        dot.dataset.node = node.id;
        dot.dataset.port = String(i);
        dot.dataset.io = 'out';
        row.appendChild(lbl);
        row.appendChild(dot);
        deps.setupPortDrag(dot);
        ports.appendChild(row);
      });
    }

    el.appendChild(ports);
    wrapper.appendChild(el);
    deps.setupNodeDrag(el, node);
  }
}
